import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { z } from "zod";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { REPORTING_ROLES, UPCLAIM_ONLY_HREFS } from "@/components/layout/nav-config";
import { ReportingShell } from "@/features/reporting/components/reporting-shell";
import { type ChartBucket, grainsForPeriod, resolveGrain } from "@/features/reporting/lib/datalake";
import { periodsForRoute, REPORTING_PERIOD_VALUES, resolvePeriod } from "@/features/reporting/lib/period";
import { useCurrentUser } from "@/hooks/use-current-user";

/**
 * `period` reste optionnel : tant qu'il est absent de l'URL, chaque sous-page applique sa
 * propre valeur par défaut (cf. `resolvePeriod`) au lieu d'un « month » global. Idem pour
 * `grain`, qui retombe sur la maille par défaut de la période (cf. `resolveGrain`).
 */
const reportingSearchSchema = z.object({
  period: z.enum(REPORTING_PERIOD_VALUES).optional().catch(undefined),
  grain: z.enum(["day", "week", "month", "quarter"]).optional().catch(undefined),
  tenantId: z.string().optional().catch(undefined),
  companyId: z.string().optional().catch(undefined),
});

/** Titre affiché par sous-page — la clé est le pathname exact. */
const REPORT_TITLES = {
  "/reporting": "Portfolio",
  "/reporting/vols": "Suivi des vols",
  "/reporting/signatures": "Funnel de signature",
  "/reporting/pilotage": "Pilotage ops",
  "/reporting/recouvrement": "Recouvrement",
  "/reporting/commercial": "Financier",
} as const;

type ReportingRoute = keyof typeof REPORT_TITLES;

const REPORTING_ROUTES = Object.keys(REPORT_TITLES) as ReportingRoute[];

/**
 * Le filtre client est masqué sur « Financier » : les tables `commercial_*`
 * portent l'ENTREPRISE facturée, pas le client traitant — il n'y a aucune colonne sur
 * laquelle appliquer ce filtre.
 */
const TENANT_FILTER_BY_ROUTE = {
  "/reporting": true,
  "/reporting/signatures": true,
  "/reporting/pilotage": false,
  "/reporting/vols": true,
  "/reporting/commercial": false,
  "/reporting/recouvrement": true,
} as const satisfies Record<ReportingRoute, boolean>;

/**
 * Seule sous-page à exposer la maille : `claim_events_daily` est un mart JOURNALIER et additif,
 * donc la même fenêtre se lit à plusieurs échelles. Les autres marts portent leur propre grain.
 */
const GRAIN_ROUTE = "/reporting/pilotage";

/** Le Portfolio se lit sur tout l'historique côté client (TECH-1182) : pas d'onglets de période. */
const UPCLAIM_PERIOD_ROUTES = new Set<ReportingRoute>(["/reporting"]);

export const Route = createFileRoute("/_authed/_dashboard/reporting")({
  validateSearch: reportingSearchSchema,
  component: ReportingLayout,
});

/**
 * Shell des sous-pages de reporting — porte les search params (période + scope) et la garde
 * d'accès, pour que les enfants ne soient que du contenu.
 */
function ReportingLayout() {
  const { can } = useCurrentUser();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  /*
   * `to` explicite, sinon la navigation se résout sur le layout (`/reporting`) : changer de
   * période depuis « Funnel de signature » renvoyait sur le Portfolio.
   */
  const currentRoute: ReportingRoute = REPORTING_ROUTES.find((route) => route === pathname) ?? "/reporting";

  /*
   * Deux niveaux : la section est ouverte aux CUSTOMER_ADMIN et CUSTOMER_ACCOUNT_MANAGER (l'API
   * les épingle à leur propre tenant), mais les sous-pages `internalOnly` restent réservées à
   * Upclaim. Le 404 plutôt qu'un 403 : une page dont on n'a pas à connaître l'existence ne
   * s'annonce pas.
   */
  const isInternalRoute = UPCLAIM_ONLY_HREFS.has(currentRoute);
  if (!can(UPCLAIM_ROLES) && (isInternalRoute || !can(REPORTING_ROLES))) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-muted-foreground">Cette page n'existe pas.</p>
      </div>
    );
  }

  const period = resolvePeriod(currentRoute, search.period);
  const showsGrain = currentRoute === GRAIN_ROUTE;
  const showTenantFilter = TENANT_FILTER_BY_ROUTE[currentRoute];

  return (
    <ReportingShell
      report={REPORT_TITLES[currentRoute]}
      period={period}
      periods={
        can(UPCLAIM_ROLES) || !UPCLAIM_PERIOD_ROUTES.has(currentRoute) ? periodsForRoute(currentRoute) : []
      }
      onPeriodChange={(period) =>
        navigate({
          to: currentRoute,
          // La maille est relative à la période : la garder pourrait laisser un « trimestre »
          // sur « 4 dernières semaines ». `resolveGrain` retomberait dessus, autant nettoyer l'URL.
          search: (prev) => ({ ...prev, period, grain: undefined }),
          replace: true,
          resetScroll: false,
        })
      }
      grain={showsGrain ? resolveGrain(period, search.grain) : undefined}
      grains={showsGrain ? grainsForPeriod(period) : undefined}
      onGrainChange={
        showsGrain
          ? (grain: ChartBucket) =>
              navigate({
                to: currentRoute,
                search: (prev) => ({ ...prev, grain }),
                replace: true,
                resetScroll: false,
              })
          : undefined
      }
      showTenantFilter={showTenantFilter}
      /*
       * Un seul endroit décide du périmètre : sans ce masquage, un `tenantId` hérité d'une
       * autre sous-page filtrerait en douce la liste d'entreprises sans être affiché nulle part.
       */
      filters={{
        tenantId: showTenantFilter ? search.tenantId : undefined,
        companyId: search.companyId,
      }}
      onFiltersChange={(filters) =>
        navigate({
          to: currentRoute,
          search: (prev) => ({
            ...prev,
            // Filtre masqué : on préserve la sélection client plutôt que de l'effacer au
            // premier changement d'entreprise (aller-retour Portfolio ↔ Commercial).
            tenantId: showTenantFilter ? filters.tenantId : prev.tenantId,
            companyId: filters.companyId,
          }),
          replace: true,
          resetScroll: false,
        })
      }
    >
      <Outlet />
    </ReportingShell>
  );
}
